/* First-party order attribution.
   Records the first useful source for 90 days and writes it to private Shopify
   cart attributes. Owner decision 2026-08-30: this limited, first-party order
   provenance remains ungated so declined analytics does not make the order
   anonymous. No raw query value, referrer hostname, account token, or customer
   identifier is kept or shared with an advertising platform. */
(() => {
  'use strict';

  // The same pure formatter is used by the bounded existing-order backfill.
  // No browser capture, cookies or network run when it is loaded in Node.
  if (typeof window === 'undefined') {
    if (typeof module !== 'undefined') module.exports = { readableOrderDetails };
    return;
  }

  function prettyDate(value) {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = String(value || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    const parsed = new Date(date + 'T00:00:00Z');
    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) return date;
    return `${parsed.getUTCDate()} ${MONTHS[parsed.getUTCMonth()]} ${parsed.getUTCFullYear()}`;
  }

  /* The owner's card in Shopify's Additional details. Titles are the attribute
     keys Shopify prints, so they are plain words. `display` carries the names
     Meta puts in its URL tags (campaign, ad set, ad) and the landing page title;
     the machine codes in `attrs` stay untouched for every reader. */
  function readableOrderDetails(attrs, display = {}) {
    const text = value => String(value == null ? '' : value).trim().slice(0, 160);
    const channels = {
      meta: 'Meta', google: 'Google', email: 'Email', direct: 'Direct', shop: 'Shop',
      chatgpt: 'ChatGPT', perplexity: 'Perplexity', gemini: 'Gemini', copilot: 'Copilot', claude: 'Claude', ai_other: 'Other AI',
      tiktok: 'TikTok', youtube: 'YouTube', referral: 'Referral', search_other: 'Other search',
      bing: 'Bing', pinterest: 'Pinterest', sms: 'SMS', x: 'X', linkedin: 'LinkedIn', campaign_other: 'Other campaign'
    };
    const kinds = { paid: 'Paid', organic: 'Organic', owned: 'Owned', free: 'Free', unknown: 'Unknown' };
    const source = (channel, kind, detail) => {
      const label = channels[text(channel).toLowerCase()];
      if (!label) return '';
      const parts = [label, kinds[text(kind).toLowerCase()] || 'Unknown'];
      if (text(detail) && text(detail).toLowerCase() !== text(channel).toLowerCase()) {
        parts.push(({ klaviyo: 'Klaviyo', google_shopping: 'Google Shopping' })[text(detail).toLowerCase()] || text(detail));
      }
      return parts.join(' · ');
    };
    const first = source(attrs.__ss_channel, attrs.__ss_kind, attrs.__ss_source);
    const last = source(attrs.__ss_last_channel, attrs.__ss_last_kind, attrs.__ss_last_source);
    const code = text(attrs.__ss_campaign);
    const campaign = text(display.campaign) || (code === 'r12-pl-sales-abo-v5' ? 'R12 · PL · SALES · ABO · v5' : code);
    const firstAd = text(display.ad);
    const lastAd = text(display.lastAd);
    const visit = first ? [text(display.landingTitle) || text(attrs.__ss_landing), prettyDate(attrs.__ss_first_seen)].filter(Boolean).join(' · ') : '';
    return {
      'Customer source': first,
      'Campaign': first ? campaign : '',
      'Ad set': first ? text(display.adset) : '',
      'Ad': first ? firstAd : '',
      'First visit': visit,
      'Latest source': last && last !== first ? last : '',
      'Latest ad': last && lastAd && lastAd !== firstAd ? lastAd : ''
    };
  }
  const READABLE_KEYS = Object.keys(readableOrderDetails({}));
  // Earlier releases wrote these; a cart still carrying them is cleared on the next write.
  const LEGACY_READABLE_KEYS = ['__Customer source', '__First campaign', '__First visit', '__Latest source'];

  const COOKIE = '_ss_attr';
  const OPTOUT = '_ss_noattr';
  const SYNC_KEY = 'ss_attr_synced';
  const COOKIE_DAYS = 90;
  const FIELD_LIMIT = 64;
  /* Names Meta writes into URL tags (campaign, ad set, ad) and page titles are
     longer than machine codes and are shown to the owner as they are. */
  const DISPLAY_LIMIT = 96;
  const PREFIX = '__ss_';

  /* sacrasoul.nl is the same store on its Dutch domain. Without it a .nl page —
     the thank-you page included — read its own previous page as a referral and
     overwrote the real last touch (found 2026-10-05). */
  const OWN_HOST = /(^|\.)(sacrasoul\.(com|pl|nl)|shopifycs\.com)$/i;
  const OWN_EXACT = /^(kebku1-7m\.myshopify\.com|checkout\.shopify\.com|pay\.shopify\.com)$/i;
  // A developer's own machine is not a referral.
  const LOCAL_HOST = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i;
  /* shop.app is two surfaces on one host: the Shop app, where a customer finds
     us, and Shop Pay, which they pass THROUGH on the way back from our own
     checkout. Only the second is a self-referral, so the path decides. Judging
     by host alone is what split this asset from the reader — one generalised
     /products to the whole host, the other generalised /checkout. */
  const SHOP_HOST = /^shop\.app$/i;
  const SHOP_CHECKOUT_PATH = /^\/(checkouts?|pay)(\/|$)/i;
  const SECRET_PATH = /^\/(account\/(reset|activate|orders|login\/multipass)|orders|checkouts|wallets|gift_cards|tools|a|apps)(\/|$)/i;
  const PUBLIC_PATH = /^\/(products|collections|pages|blogs|search|cart|policies)(\/|$)/i;
  const LOCALE_PATH = /^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const CLICK_IDS = [
    ['gclid', 'google', 'paid'],
    ['gbraid', 'google', 'paid'],
    ['wbraid', 'google', 'paid'],
    ['fbclid', 'meta', 'free'],
    ['ttclid', 'tiktok', 'paid'],
    ['msclkid', 'bing', 'paid'],
    ['epik', 'pinterest', 'paid'],
    ['li_fat_id', 'linkedin', 'paid'],
    ['twclid', 'x', 'paid']
  ];

  const SOURCE_RULES = [
    [/^(facebook|fb|ig|instagram|meta|facebook\.com|instagram\.com|m\.facebook\.com)$/i, 'meta'],
    [/^(google|google\.com|googleads|adwords|google_ads|google_shopping)$/i, 'google'],
    [/^(youtube|youtube\.com|yt)$/i, 'youtube'],
    [/^(shop_app|shop)$/i, 'shop'],
    [/^direct$/i, 'direct'],
    [/^(chatgpt|chatgpt\.com|openai|openai\.com)$/i, 'chatgpt'],
    [/^(perplexity|perplexity\.ai)$/i, 'perplexity'],
    [/^(gemini|gemini\.google\.com)$/i, 'gemini'],
    [/^(copilot|copilot\.microsoft\.com|copilot\.com|copilot\.cloud\.microsoft)$/i, 'copilot'],
    [/^(claude|claude\.ai)$/i, 'claude'],
    [/^(bing|microsoft|msn)$/i, 'bing'],
    [/^(tiktok|tiktok\.com)$/i, 'tiktok'],
    [/^(pinterest|pinterest\.com)$/i, 'pinterest'],
    [/^(klaviyo|email|newsletter)$/i, 'email'],
    [/^(twitter|x|x\.com)$/i, 'x'],
    [/^linkedin$/i, 'linkedin']
  ];

  const HOST_RULES = [
    [/^(chatgpt\.com|chat\.openai\.com|openai\.com)$/i, 'chatgpt'],
    [/^(www\.)?perplexity\.ai$/i, 'perplexity'],
    /* Each assistant is its own channel, ahead of the Google and Bing rules:
       gemini.google.com is not search. Same rules as where-from.py. */
    [/^gemini\.google\.com$/i, 'gemini'],
    [/^(copilot\.microsoft\.com|copilot\.com|copilot\.cloud\.microsoft)$/i, 'copilot'],
    [/^claude\.ai$/i, 'claude'],
    [/(^|\.)shop\.app$/i, 'shop'],
    [/(^|\.)(facebook\.com|instagram\.com|fb\.me|fb\.com|threads\.net)$/i, 'meta'],
    [/(^|\.)(youtube\.com|youtu\.be)$/i, 'youtube'],
    [/(^|\.)tiktok\.com$/i, 'tiktok'],
    [/(^|\.)pinterest\.(?:[a-z]{2,3}|com?\.[a-z]{2})$/i, 'pinterest'],
    [/(^|\.)(twitter\.com|x\.com|t\.co)$/i, 'x'],
    [/(^|\.)linkedin\.com$/i, 'linkedin'],
    [/^com\.google\.android\.gm$/i, 'email'],
    [/(^|\.)(mail\.google\.com|outlook\.(?:(?:live|office|office365)\.com|[a-z]{2,3}|com?\.[a-z]{2})|mail\.yahoo\.com)$/i, 'email'],
    /* google.<tld>, not google.anything: `google\.[a-z.]+$` also matched
       google.evil.com and credited an attacker-chosen domain to Google.
       google.com, google.pl, google.co.uk and google.com.hk all pass. */
    [/(^|\.)google\.(?:[a-z]{2,3}|com?\.[a-z]{2})$/i, 'google'],
    [/(^|\.)(bing\.com|duckduckgo\.com|yahoo\.(?:[a-z]{2,3}|com?\.[a-z]{2})|yandex\.(?:[a-z]{2,3}|com?\.[a-z]{2})|ecosia\.org|baidu\.com|brave\.com)$/i, 'search_other']
  ];

  const CAMPAIGN_RULES = [
    [/^per-fumum-v2$/i, 'per-fumum-v2'],
    [/^R12 · PL · SALES · ABO · v5$/i, 'r12-pl-sales-abo-v5'],
    [/^sacred_welcome_r4h$/i, 'welcome-r4h'],
    [/^sunset-(1|2)$/i, 'sunset'],
    [/^pp-r19-e[1-6]-(en|pl)$/i, 'pp-r19'],
    [/^jade oil (—|-|–) pl (—|-|–) prospecting \(cbo\)( \[v1\])?$/i, 'jade-oil-pl-prospecting-cbo']
  ];

  /* Meta appends utm_term={{adset.id}} to every ad click by itself, while
     utm_adset={{adset.name}} exists only where our own URL tags write it. Ads
     reusing a post, and some older ads, carry the id alone, and their orders
     reached the owner with no ad set at all (6 of 85 Meta orders to
     2026-10-05). When the name is missing the id stands in, named from this
     table the way readableOrderDetails names the campaign code; an id not in
     the table is shown as itself, which Ads Manager can still search. Names as
     Meta reported them on 2026-10-05. Same table as META_ADSET_NAMES in
     services/attribution/where-from.py. */
  const META_ADSET_NAMES = {
    '120249719423800207': 'R12 PL TEST-PROOF 180',
    '120249719423300207': 'R12 PL LANE 30',
    '120250082175010207': 'R19 PL FRESH 144',
    '120241210750660207': 'Broad Poland'
  };
  const META_ID = /^\d{15,20}$/;
  function metaAdsetFromTerm(source, term) {
    const id = clean(term);
    if (!META_ID.test(id) || sourceName(clean(source)) !== 'meta') return '';
    return META_ADSET_NAMES[id] || id;
  }

  const PAID_MEDIUM = /^(cpc|ppc|paid|paidsocial|paid_social|paid-social|cpm|display|retargeting|remarketing)$/i;
  const EMAIL_MEDIUM = /^(email|e-mail|newsletter|klaviyo)$/i;
  const SMS_MEDIUM = /^(sms|mms|whatsapp)$/i;
  const CHANNELS = new Set([
    'direct', 'google', 'meta', 'youtube', 'shop', 'chatgpt', 'perplexity',
    'gemini', 'copilot', 'claude', 'ai_other', 'bing', 'tiktok', 'pinterest', 'email', 'sms', 'x',
    'linkedin', 'search_other', 'referral', 'campaign_other'
  ]);
  const KINDS = new Set(['unknown', 'paid', 'free', 'owned', 'organic']);
  const SOURCES = new Set([...CHANNELS, 'google_shopping', '']);
  const CAMPAIGNS = new Set(CAMPAIGN_RULES.map(([, name]) => name));

  const clean = (value) => String(value == null ? '' : value).trim().slice(0, FIELD_LIMIT);
  /* Owner-facing text: printable characters only, whitespace collapsed, never a
     URL (a tag holding a link is a mistake, not a name), bounded in length. */
  function label(value) {
    const text = String(value == null ? '' : value).replace(/[\u0000-\u001f\u007f<>"]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!text || /[a-z][a-z0-9+.-]*:\/\//i.test(text)) return '';
    return text.slice(0, DISPLAY_LIMIT);
  }
  function landingTitle() {
    try {
      const title = String(document.title || '');
      return label(title.split(/\s+[\u2013\u2014|-]\s+/)[0]);
    } catch (error) { return ''; }
  }

  function query() {
    try { return new URL(window.location.href).searchParams; }
    catch (error) { return new URLSearchParams(); }
  }

  function hostOf(value) {
    try {
      /* Only when there is a scheme. `new URL('localhost:3000')` does not
         throw — it parses as scheme `localhost:` with an EMPTY hostname, so
         the fallback below never ran and a ported bare host classified
         differently here than in the reader. */
      if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
        const host = new URL(value).hostname.replace(/^www\./i, '');
        if (host) return host;
      }
      throw new Error('bare');
    } catch (error) {
      /* A bare host, which is how Shopify records some referrers and how a
         utm_source is sometimes written. Port stripped, as URL.hostname does. */
      const bare = String(value || '').trim().split('/')[0].split('?')[0]
        .split('@').pop();  // URL.hostname drops userinfo; this must too
      const host = bare.replace(/:\d+$/, '').replace(/^www\./i, '');
      return /^[a-z0-9.-]+$/i.test(host) || LOCAL_HOST.test(host) ? host : '';
    }
  }

  /* Schemed and bare inputs normalise to the same host and path before the
     rule is applied. Requiring a scheme meant a bare shop.app/checkout/x —
     which both sides otherwise accept as a source — read as a Shop
     acquisition rather than a Shop Pay hop. */
  function pathOf(value) {
    try {
      if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
        const url = new URL(value);
        if (url.hostname) return url.pathname;
      }
    } catch (error) {}
    const bare = String(value || '').split('?')[0].split('#')[0];
    const slash = bare.indexOf('/');
    return slash === -1 ? '/' : bare.slice(slash) || '/';
  }

  function checkoutHop(value) {
    const host = hostOf(value);
    return Boolean(host) && SHOP_HOST.test(host) && SHOP_CHECKOUT_PATH.test(pathOf(value));
  }

  function referrerHost() { return hostOf(document.referrer); }

  function returnedThroughCheckout() { return checkoutHop(document.referrer); }

  function safeLanding(rawPath) {
    try {
      let path = String(rawPath || '/');
      const locale = path.match(LOCALE_PATH);
      const prefix = locale ? locale[0] : '';
      if (prefix) path = path.slice(prefix.length) || '/';
      if (path === '/[private]') return clean(prefix + path);
      if (SECRET_PATH.test(path)) return clean(`${prefix}/[private]`);
      if (path === '/' || PUBLIC_PATH.test(path)) return clean(prefix + path);
    } catch (error) {}
    return '/[unknown]';
  }

  function landingPath() {
    try { return safeLanding(window.location.pathname); }
    catch (error) { return '/[unknown]'; }
  }

  function ownHost(host) {
    return Boolean(host && (OWN_HOST.test(host) || OWN_EXACT.test(host) || LOCAL_HOST.test(host)));
  }

  function sourceName(value) {
    for (const [rule, name] of SOURCE_RULES) {
      if (rule.test(value)) return name;
    }
    return 'campaign_other';
  }

  function campaignName(value) {
    const candidate = clean(value);
    for (const [rule, name] of CAMPAIGN_RULES) {
      if (rule.test(candidate)) return name;
    }
    return '';
  }

  /* The tail of the ladder, rung for rung with finish_kind() in
     services/attribution/where-from.py — including the part where an owned
     medium OVERRIDES the channel its source claimed. A Klaviyo send tagged
     utm_source=facebook is an email, not a Meta acquisition. This file used to
     apply that override on one of its three branches and read the medium on
     two, so a differential run of 730 inputs disagreed with the reader on 65:
     utm_medium=cpc beside a google.com referrer was organic here and paid
     there, and utm_medium=email beside a facebook.com referrer stayed meta.

     A medium that is present and not paid is free; an ABSENT medium is unknown,
     not free. Calling it free records that advertising did not produce the
     order, permanently, on no evidence. */
  function finishKind(channel, medium) {
    if (EMAIL_MEDIUM.test(medium)) return ['email', 'owned'];
    if (SMS_MEDIUM.test(medium)) return ['sms', 'owned'];
    if (PAID_MEDIUM.test(medium)) return [channel, 'paid'];
    return [channel, medium ? 'free' : 'unknown'];
  }

  /* Resolved through a HOST, so the visit demonstrably came from there: an
     otherwise-unknown kind is organic. The reader's `kind if kind != "unknown"
     else "organic"` rung. */
  function fromHost(channel, medium, base) {
    const [ch, kind] = finishKind(channel, medium);
    return { ...base, ch, kind: kind === 'unknown' ? 'organic' : kind, src: ch };
  }

  function touchFromPage() {
    const params = query();
    const medium = clean(params.get('utm_medium'));
    const campaign = campaignName(params.get('utm_campaign'));
    const base = {
      ch: 'direct',
      kind: 'unknown',
      src: '',
      cmp: campaign,
      lp: landingPath(),
      at: new Date().toISOString().slice(0, 10),
      /* Names for the owner's card, exactly as the URL tags say them. Meta ads
         carry utm_campaign={{campaign.name}}, utm_adset={{adset.name}},
         utm_content={{ad.name}}; these never feed a classifier. */
      cn: label(params.get('utm_campaign')),
      as: label(params.get('utm_adset')) || label(metaAdsetFromTerm(params.get('utm_source'), params.get('utm_term'))),
      ad: label(params.get('utm_content')),
      lt: landingTitle()
    };

    for (const [parameter, channel, defaultKind] of CLICK_IDS) {
      if (!params.get(parameter)) continue;
      const kind = defaultKind === 'paid' || PAID_MEDIUM.test(medium) ? 'paid' : defaultKind;
      return { ...base, ch: channel, kind, src: channel };
    }

    if (params.get('srsltid')) {
      return { ...base, ch: 'google', kind: 'free', src: 'google_shopping' };
    }

    const rawSource = clean(params.get('utm_source'));
    if (rawSource) {
      let channel = sourceName(rawSource);
      if (channel === 'direct') return base;
      /* The reader classifies a source that is really a URL or host through the
         host rules; without this, utm_source=https://shop.app/checkout was
         campaign_other here and blind there. */
      /* Same predicate as classify() in where-from.py: a scheme, or a dot.
         LOCAL_HOST has no dot, so it is named explicitly or the two sides
         part company on utm_source=localhost. */
      if (channel === 'campaign_other' &&
          (/^[a-z][a-z0-9+.-]*:\/\//i.test(rawSource) || rawSource.includes('.') ||
           LOCAL_HOST.test(rawSource))) {
        const sourceHost = hostOf(rawSource);
        if (sourceHost) {
          if (ownHost(sourceHost) || checkoutHop(rawSource)) return base;
          for (const [rule, name] of HOST_RULES) {
            if (rule.test(sourceHost)) { channel = name; break; }
          }
          if (channel === 'campaign_other') channel = 'referral';
          return fromHost(channel, medium, base);
        }
      }
      const [ch, kind] = finishKind(channel, medium);
      return { ...base, ch, kind, src: ch };
    }

    const host = referrerHost();
    if (host && !ownHost(host) && !returnedThroughCheckout()) {
      for (const [rule, channel] of HOST_RULES) {
        if (rule.test(host)) return fromHost(channel, medium, base);
      }
      return fromHost('referral', medium, base);
    }

    return base;
  }

  function readCookie(name) {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|;)\\s*${name}=([^;]+)`));
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) { return ''; }
  }

  function writeCookie(name, value, seconds) {
    try {
      const secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${name}=${encodeURIComponent(value)};Max-Age=${Math.max(0, Math.floor(seconds))};Path=/;SameSite=Lax${secure}`;
    } catch (error) {}
  }

  function clearCookie(name) {
    writeCookie(name, '', 0);
  }

  function normalizeTouch(touch, partial = false) {
    if (!touch || Object.values(touch).some((value) => typeof value === 'string' && value.length > DISPLAY_LIMIT)) return null;
    const channel = clean(touch.ch);
    const kind = clean(touch.kind);
    const source = clean(touch.src);
    if (!CHANNELS.has(channel) || !KINDS.has(kind) || !SOURCES.has(source)) return null;
    const names = {};
    for (const key of ['cn', 'as', 'ad', 'lt']) {
      const value = label(touch[key]);
      if (value) names[key] = value;
    }
    if (partial) return { ch: channel, kind, src: source, partial: true, ...names };
    const campaign = clean(touch.cmp);
    const landing = clean(touch.lp);
    const date = clean(touch.at);
    if ((campaign && !CAMPAIGNS.has(campaign)) || safeLanding(landing) !== landing ||
        !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
    return { ch: channel, kind, src: source, cmp: campaign, lp: landing, at: date, ...names };
  }

  /* The same record, mirrored in localStorage. Measured 2026-09-23: in the
     Instagram and Facebook in-app browsers on iOS, 101 of 1,415 Meta landings
     in 14 days (7%) ran this file yet could not read the cookie back on the
     landing page or the next one. The shopper was then filed as Direct, or the
     order carried no source at all (#2141, #2145, #2200, #2264, #2252, #2307).
     PostHog's localStorage identity and ss-identity.js's storage cache survived
     the same page loads, so storage is the fallback, never the first choice.
     Same fields, same expiry, same opt-out and internal rules as the cookie. */
  const MIRROR = 'ss_attr';

  function readMirror() {
    try { return localStorage.getItem(MIRROR) || ''; } catch (error) { return ''; }
  }

  /* And this page's own copy, in memory, as the last resort. Measured in
     PostHog on 2026-10-05: 300 customer pageviews on sacrasoul.pl in 7 days
     carried no ss_channel. Every one was iOS (219 Facebook in-app browser, 75
     Safari, 6 Chrome; none on Android), 281 were the only page of their visit,
     and nearly all were ad landings whose URL said utm_source=facebook.
     ss_market, registered in the same call, was present, so PostHog did ask;
     SSAttribution.properties() answered empty, which it does only when the
     store cannot be read back. In the 17 visits that went on to a second page,
     that page had the channel. The reading that fits: on those landings
     neither the cookie nor the mirror could be read back on the page that
     wrote them (not reproduced on a device). The record computed on this page
     is therefore kept here too, so the page can always answer for itself —
     its pageview, cart sync and owner rows. It is written nowhere, dies with
     the page, and passes the same expiry check as the stored copies. */
  let memory = '';

  function forgetStore() {
    memory = '';
    clearCookie(COOKIE);
    try { localStorage.removeItem(MIRROR); } catch (error) {}
  }

  function loadStore() {
    return parseStore(readCookie(COOKIE)) || parseStore(readMirror()) || parseStore(memory);
  }

  function parseStore(text) {
    try {
      const raw = JSON.parse(text);
      const now = Date.now();
      if (!raw || raw.v !== 1 || !Number.isFinite(raw.exp) || raw.exp <= now ||
          raw.exp > now + COOKIE_DAYS * 864e5 + 300000) return null;
      const first = normalizeTouch(raw.ft);
      const last = normalizeTouch(raw.lt || raw.ft, Boolean(raw.lt && raw.lt.partial));
      if (!first || !last) return null;
      const store = { ft: first, lt: last, exp: raw.exp, v: 1 };
      if (raw.hy === 1) store.hy = 1;  // rebuilt from the cart: see visitorKind()
      return store;
    } catch (error) { return null; }
  }

  /* `.pl` and `.com` cannot share cookies or localStorage. Shopify can carry one
     cart between them, though, and its private attributes are the only first-touch
     handoff both domains can read. Adopt that answer only when this browser has no
     useful history of its own. The cart deliberately stores no last landing,
     campaign, or timestamp, so the partial last touch stays partial rather than
     borrowing first-touch facts that never described it. */
  function hydrateStoreFromCart() {
    try {
      const current = loadStore();
      if (current && useful(current.ft)) return current;
      const cart = window.SacrasoulCartInitialCart;
      const attrs = cart && cart.item_count ? cart.attributes : null;
      if (!attrs || typeof attrs !== 'object') return current;

      const first = normalizeTouch({
        ch: attrs[PREFIX + 'channel'],
        kind: attrs[PREFIX + 'kind'],
        src: attrs[PREFIX + 'source'],
        cmp: attrs[PREFIX + 'campaign'],
        lp: attrs[PREFIX + 'landing'],
        at: attrs[PREFIX + 'first_seen']
      });
      const last = normalizeTouch({
        ch: attrs[PREFIX + 'last_channel'],
        kind: attrs[PREFIX + 'last_kind'],
        src: attrs[PREFIX + 'last_source']
      }, true);
      if (!first || !last) return current;

      const firstSeen = Date.parse(first.at + 'T00:00:00Z');
      const now = Date.now();
      const exp = firstSeen + COOKIE_DAYS * 864e5;
      if (!Number.isFinite(firstSeen) || firstSeen > now || exp <= now ||
          new Date(firstSeen).toISOString().slice(0, 10) !== first.at ||
          exp > now + COOKIE_DAYS * 864e5) return current;
      return saveStore({ ft: first, lt: last, exp, v: 1, hy: 1 });
    } catch (error) { return loadStore(); }
  }

  function saveStore(store) {
    if (!store.exp) store.exp = Date.now() + COOKIE_DAYS * 864e5;
    const seconds = (store.exp - Date.now()) / 1000;
    if (seconds <= 0) { forgetStore(); return null; }
    const text = JSON.stringify(store);
    memory = text;
    writeCookie(COOKIE, text, seconds);
    try { localStorage.setItem(MIRROR, text); } catch (error) {}
    return store;
  }

  function useful(touch) {
    return touch && touch.ch !== 'direct';
  }

  /* Our own visits are not acquisitions. The same signal ss-posthog.js uses to
     set ss_internal — the shared key, Shopify's design mode, the preview bar —
     decides it here, so one opt-in silences both. Measured 2026-09-01: 139 of
     1,489 sessions in 90 days were flagged internal, 13 of them carrying
     utm_source=qa, which this asset was filing as a campaign. */
  const INTERNAL_KEY = 'ss_internal';

  /* One marker, two readers. This file and ss-posthog.js read the storage key;
     the server container (Meta CAPI, Klaviyo, GA4 via pulse.*) can only read a
     COOKIE named ss_internal=1, and until 2026-10-05 nothing in the browser
     wrote one, so a device we had marked internal was still sent to Meta. Seen
     here, the flag is also written as that cookie, on the shop's own
     registrable domain so pulse.<domain> receives it, and re-written on every
     internal page: Safari caps a cookie written by script at 7 days, so only
     the re-write keeps it alive. The name and value are the ones the server
     template "Internal Marker Resolver" reads and tests/browser/_internal.mjs
     sets on probes; 400 days is the longest life a browser grants. Clearing
     (?ss_internal=0) is ss-posthog.js's job, under its same-origin rule, and
     ss-posthog.js writes the same cookie with the same rule. */
  const INTERNAL_COOKIE_SECONDS = 34560000;

  function markInternalCookie() {
    try {
      const host = String(window.location.hostname || '');
      const own = host.match(/(?:^|\.)(sacrasoul\.(?:com|pl|nl))$/i);
      const domain = own ? `;Domain=.${own[1].toLowerCase()}` : '';
      const secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${INTERNAL_KEY}=1;Max-Age=${INTERNAL_COOKIE_SECONDS};Path=/;SameSite=Lax${domain}${secure}`;
    } catch (error) {}
  }

  function internalVisit() {
    try {
      const flag = query().get(INTERNAL_KEY);
      /* Honoured wherever it arrives from, unlike the same read in
         ss-posthog.js, which requires a same-origin hop. The failure modes are
         not symmetric. Rejecting it cross-origin loses nothing but a QA link
         opened from a doc tool — and that visit is then RECORDED, with
         docs.google.com landing as a Google acquisition, which is the exact
         pollution this exists to stop. Honouring it can only ever drop a touch,
         never invent a channel, and a forwarded link costs one unrecorded visit
         rather than a fabricated one. Losing data beats inventing it. `0` is
         the one direction that could add a touch, so it lifts the remembered
         flag only and leaves design mode and the preview bar to decide.
         Never delete the parameter here — ss-posthog.js runs after this file
         and still has to read it. */
      if (flag === '1') return true;
      /* `0` lifts a remembered flag; it does NOT out-vote the preview bar or
         design mode below, which are facts about the page rather than a claim
         in a link. Skipping straight past them here would let the parameter
         create a touch instead of only ever dropping one. */
      if (flag !== '0') {
        let remembered = readCookie(INTERNAL_KEY) === '1';
        try { if (sessionStorage.getItem(INTERNAL_KEY) === '1') remembered = true; } catch (error) {}
        try { if (localStorage.getItem(INTERNAL_KEY) === '1') remembered = true; } catch (error) {}
        if (remembered) { markInternalCookie(); return true; }
      }
    } catch (error) {}
    try {
      if (window.Shopify && window.Shopify.designMode) return true;
      if (document.getElementById('preview-bar-iframe')) return true;
    } catch (error) {}
    /* The Paseo agent shell, keyed on `Paseo/` exactly as ss-posthog.js
       internalShell() does. Without it the shell wrote real first touches and
       owner rows (found 2026-09-27; the two files had drifted since c12b0255c). */
    try {
      if ((navigator.userAgent || '').indexOf('Paseo/') !== -1) return true;
    } catch (error) {}
    return false;
  }

  function recordTouch() {
    /* Record nothing at all rather than a touch we would have to exclude later:
       an internal visit must not become a first touch, nor overwrite a real one. */
    if (internalVisit()) return loadStore();
    const touch = touchFromPage();
    const store = loadStore();
    if (!store) return saveStore({ ft: touch, lt: touch, exp: Date.now() + COOKIE_DAYS * 864e5, v: 1 });
    if (!useful(touch)) return store;
    store.lt = touch;
    if (!useful(store.ft)) store.ft = touch;
    return saveStore(store);
  }

  function optedOut() {
    // The page carrying the request itself, even where nothing can be stored.
    if (query().get('ss_noattr') === '1') return true;
    try { if (localStorage.getItem(OPTOUT) === '1') return true; }
    catch (error) {}
    return readCookie(OPTOUT) === '1';
  }

  function posthogId() {
    try {
      if (!window.SSPosthogAnalyticsAllowed || window.SSPosthogAnalyticsAllowed() !== true) return '';
      const posthog = window.posthog;
      const id = clean(posthog && posthog.get_distinct_id && posthog.get_distinct_id());
      const device = clean(posthog && posthog.get_property && posthog.get_property('$device_id'));
      return UUID.test(id) && id === device ? id : '';
    } catch (error) { return ''; }
  }

  const ATTRIBUTE_NAMES = [
    'channel', 'kind', 'source', 'campaign', 'landing', 'first_seen',
    'last_channel', 'last_kind', 'last_source', 'ph'
  ];

  function emptyAttributes() {
    return Object.fromEntries([...READABLE_KEYS, ...LEGACY_READABLE_KEYS, ...ATTRIBUTE_NAMES.map((name) => PREFIX + name)].map((key) => [key, '']));
  }

  function orderAttributes() {
    /* Opt-out is checked BEFORE internal on purpose. A deletion-only write is
       the one write an internal browser should still make: if that browser is
       also opted out, stale fields already sitting on the cart must come off,
       and refusing the clear because the visitor happens to be us would leave
       real data behind. Deleting outranks not-writing. */
    if (optedOut()) return emptyAttributes();
    /* Recording nothing is not enough. An internal browser that already holds a
       valid _ss_attr cookie from an earlier real visit would otherwise keep
       writing it onto every cart it touches, which is the pollution this exists
       to stop — and cart attributes are permanent order data. Nothing is
       cleared: the cookie is that browser's own history and stays its own. */
    if (internalVisit()) return null;
    const store = loadStore();
    if (!store || !store.ft) return null;
    const first = store.ft;
    const last = store.lt || first;
    const raw = {
      [PREFIX + 'channel']: first.ch,
      [PREFIX + 'kind']: first.kind,
      [PREFIX + 'source']: first.src || '',
      [PREFIX + 'campaign']: first.cmp || '',
      [PREFIX + 'landing']: first.lp || '',
      [PREFIX + 'first_seen']: first.at || '',
      [PREFIX + 'last_channel']: last.ch,
      [PREFIX + 'last_kind']: last.kind,
      [PREFIX + 'last_source']: last.src || '',
      [PREFIX + 'ph']: posthogId()
    };
    const readable = readableOrderDetails(raw, {
      campaign: first.cn, adset: first.as, ad: first.ad, landingTitle: first.lt, lastAd: last.ad
    });
    // A cart written by an earlier release still carries the old titles; clear them.
    return { ...Object.fromEntries(LEGACY_READABLE_KEYS.map((key) => [key, ''])), ...readable, ...raw };
  }

  function fingerprint(attributes) {
    return Object.keys(attributes).sort().map((key) => `${key}=${attributes[key]}`).join('&');
  }

  function cartKey() {
    try {
      const raw = readCookie('cart');
      let hash = 2166136261;
      for (let index = 0; index < raw.length; index += 1) {
        hash ^= raw.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return (hash >>> 0).toString(36);
    } catch (error) { return ''; }
  }

  let cartKnownNonEmpty = false;
  try { cartKnownNonEmpty = Boolean(window.SacrasoulCartInitialCart && window.SacrasoulCartInitialCart.item_count); }
  catch (error) {}

  const writes = new Map();

  async function syncCart(knownNonEmpty = false) {
    if (knownNonEmpty) cartKnownNonEmpty = true;
    if (!cartKnownNonEmpty) return false;

    const attributes = orderAttributes();
    if (!attributes) return false;
    const answer = fingerprint(attributes);
    const key = `${cartKey()}|${answer}`;
    try { if (sessionStorage.getItem(SYNC_KEY) === key) return true; }
    catch (error) {}
    if (writes.has(key)) return writes.get(key);

    const write = (async () => {
      try {
        let cart = window.SacrasoulCart;
        if (cart && cart.__isBoot && typeof cart.load === 'function') cart = await cart.load();
        if (!cart || typeof cart.setAttributes !== 'function') return false;

        const guard = () => {
          const current = orderAttributes();
          return Boolean(current && fingerprint(current) === answer);
        };

        await cart.setAttributes(attributes, { guard });
        try { sessionStorage.setItem(SYNC_KEY, key); } catch (error) {}
        return true;
      } catch (error) { return false; }
    })();

    writes.set(key, write);
    write.finally(() => writes.delete(key));
    return write;
  }

  const PROPERTY_NAMES = [
    'ss_channel', 'ss_kind', 'ss_source', 'ss_campaign', 'ss_landing',
    'ss_first_seen', 'ss_last_channel', 'ss_last_kind', 'ss_last_source',
    'ss_adset', 'ss_ad', 'ss_last_ad', 'ss_visitor'
  ];

  /* New or returning, from the record above and nothing else: it is written for
     every visitor whatever their consent, so this answer exists for cookieless
     PostHog events too, which have no person to look up. The record's expiry is
     set once, when it is first written, and never moved, so it dates the first
     touch to the millisecond. Older than PostHog's 30-minute session window
     means this browser was here in an earlier visit. A first visit that runs
     past 30 minutes turns returning on the pages after that; that is the price
     of adding no new storage. */
  const SESSION_MS = 30 * 60 * 1000;
  /* Except for a record rebuilt from the cart (hy: 1, hydrateStoreFromCart).
     Its expiry was derived from the first-seen DATE, not written at the first
     touch, so the subtraction would date a 10:00 landing to midnight and call
     its 10:05 second page returning — in exactly the browsers that keep
     neither copy (the iOS in-app population) and on every .pl<->.com hop.
     The first touch happened at some moment of that day, so the latest it can
     have been is the day's end: only a day that ended more than a session
     window ago proves an earlier visit. */
  function visitorKind(store) {
    // A rebuilt record (hy: 1) pins exp to its first-seen DAY; recordTouch may later
    // replace a direct ft with today's touch, so date it from exp, never from ft.at.
    const firstWritten = store.hy === 1
      ? store.exp - COOKIE_DAYS * 864e5 + 864e5
      : store.exp - COOKIE_DAYS * 864e5;
    if (!Number.isFinite(firstWritten)) return 'new';
    return Date.now() - firstWritten > SESSION_MS ? 'returning' : 'new';
  }

  function properties() {
    if (optedOut()) return {};
    const store = loadStore();
    if (!store || !store.ft) return {};
    const first = store.ft;
    const last = store.lt || first;
    return {
      ss_visitor: visitorKind(store),
      ss_channel: first.ch,
      ss_kind: first.kind,
      ss_source: first.src || null,
      ss_campaign: first.cmp || null,
      ss_landing: first.lp || null,
      ss_first_seen: first.at || null,
      ss_last_channel: last.ch,
      ss_last_kind: last.kind,
      ss_last_source: last.src || null,
      ss_adset: first.as || null,
      ss_ad: first.ad || null,
      ss_last_ad: last.ad || null
    };
  }

  function unregisterPosthog() {
    try {
      if (!window.posthog || typeof window.posthog.unregister !== 'function') return;
      for (const name of PROPERTY_NAMES) window.posthog.unregister(name);
    } catch (error) {}
  }

  function setApi(store) {
    window.SSAttribution = {
      off: optedOut(),
      get: loadStore,
      touch: () => (loadStore() || {}).ft || null,
      last: () => (loadStore() || {}).lt || null,
      properties,
      attributes: orderAttributes,
      sync: syncCart,
      ready: Promise.resolve(store)
    };
  }

  const escape = query().get('ss_noattr');
  if (escape === '1') {
    try { localStorage.setItem(OPTOUT, '1'); } catch (error) {}
    writeCookie(OPTOUT, '1', 365 * 86400);
  }

  /* Prerender gate (2026-09-11): product links are prerendered. A prerendered PDP
     must not record a first touch or sync the cart for a page the shopper never
     activated; boot waits for prerenderingchange there, runs at once elsewhere. */
  function boot() {
  let store = null;
  if (optedOut()) {
    forgetStore();
    unregisterPosthog();
  } else {
    if (!internalVisit()) hydrateStoreFromCart();
    store = recordTouch();
  }
  setApi(store);

  window.addEventListener('storage', (event) => {
    if (!event || event.key !== OPTOUT) return;
    if (optedOut()) {
      forgetStore();
      unregisterPosthog();
    } else {
      store = recordTouch();
    }
    try { sessionStorage.removeItem(SYNC_KEY); } catch (error) {}
    setApi(store);
    if (cartKnownNonEmpty) syncCart();
  });

  const itemEvents = new Set([
    'cart_item_added', 'cart_gift_added', 'cart_item_restored', 'cart_review_requested'
  ]);
  document.addEventListener('sacrasoul:behavior', (event) => {
    const name = event && event.detail && event.detail.name;
    if (!itemEvents.has(name)) return;
    cartKnownNonEmpty = true;
    syncCart();
  });

  const syncExistingCart = () => { if (cartKnownNonEmpty || optedOut()) syncCart(cartKnownNonEmpty); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncExistingCart, { once: true });
  } else {
    syncExistingCart();
  }
  }
  if (document.prerendering) document.addEventListener('prerenderingchange', boot, { once: true });
  else boot();
})();
